import type { AssistantMessage, AssistantToolActivity } from '@/api/assistant/assistantApi.types';
import type { AssistantAutomaticActionResult, AssistantLiveTurn } from '@/hooks/useAssistantTurn';
import { AssistantReply } from './AssistantReply';
import { AssistantUserBubble } from './AssistantUserBubble';

interface AssistantMessageListProps {
  messages: AssistantMessage[];
  liveTurn: AssistantLiveTurn | null;
}

const NO_ACTIVITIES: AssistantToolActivity[] = [];

function isStoredMessage(messages: AssistantMessage[], messageId: string | null): boolean {
  return (
    messageId !== null &&
    messages.some(function matches(message) {
      return (
        message.id === messageId && message.status !== 'pending' && message.status !== 'streaming'
      );
    })
  );
}

const ACTION_NOTICE_KEYS: Partial<Record<AssistantAutomaticActionResult, string>> = {
  unsaved_changes: 'actions.notMovedUnsaved',
  target_missing: 'actions.targetMissing',
  unavailable: 'actions.unavailable',
};

function resolveActionNoticeKey(result: AssistantAutomaticActionResult | null): string | null {
  if (result === null) {
    return null;
  }
  const noticeKey = ACTION_NOTICE_KEYS[result];
  return noticeKey === undefined ? null : noticeKey;
}

function renderStoredMessage(message: AssistantMessage, liveTurn: AssistantLiveTurn | null) {
  if (message.role === 'user') {
    return <AssistantUserBubble key={message.id} text={message.text} />;
  }
  if (message.status === 'pending' || message.status === 'streaming') {
    return null;
  }
  return (
    <AssistantReply
      key={message.id}
      messageId={message.id}
      text={message.text}
      activities={NO_ACTIVITIES}
      actions={message.uiActions}
      isWaiting={false}
      isStreaming={false}
      outcome={message.outcome}
      errorCode={message.errorCode}
      errorMessage=""
      feedback={message.feedback}
      actionNoticeKey={
        liveTurn !== null && liveTurn.turnId === message.id
          ? resolveActionNoticeKey(liveTurn.automaticActionResult)
          : null
      }
    />
  );
}

export function AssistantMessageList({ messages, liveTurn }: AssistantMessageListProps) {
  const showLiveQuestion =
    liveTurn !== null &&
    (liveTurn.userMessage === null || isStoredMessage(messages, liveTurn.userMessage.id) === false);
  const showLiveReply = liveTurn !== null && isStoredMessage(messages, liveTurn.turnId) === false;

  return (
    <div className="px-4 py-4 space-y-5">
      {messages.map(function renderMessage(message) {
        return renderStoredMessage(message, liveTurn);
      })}
      {liveTurn !== null && showLiveQuestion ? (
        <AssistantUserBubble text={liveTurn.question} />
      ) : null}
      {liveTurn !== null && showLiveReply ? (
        <AssistantReply
          messageId={null}
          text={liveTurn.text}
          activities={liveTurn.activities}
          actions={liveTurn.actionsAwaitingConfirmation}
          isWaiting={liveTurn.status === 'asking' || liveTurn.status === 'streaming'}
          isStreaming={liveTurn.status === 'streaming' && liveTurn.text.length > 0}
          outcome={liveTurn.outcome}
          errorCode={liveTurn.errorCode}
          errorMessage={liveTurn.errorMessage}
          feedback={null}
          actionNoticeKey={resolveActionNoticeKey(liveTurn.automaticActionResult)}
        />
      ) : null}
    </div>
  );
}
